/** Рейс для итогов года: только состоявшиеся и не отменённые, отбирает вызывающий. */
export interface YearRow {
  depLocalDate: string;
  depIata: string;
  depCity: string;
  depCountry: string;
  arrIata: string;
  arrCity: string;
  arrCountry: string;
  airlineCode: string | null;
  distanceKm: number | null;
  durationMin: number | null;
  estimated?: boolean;
}

export interface YearSummary {
  year: number;
  flights: number;
  distanceKm: number;
  durationMin: number;
  /** Есть рейсы с прилётом, оценённым по расстоянию: часы показывать с «≈». */
  estimated: boolean;
  /** Страны года в порядке первого появления в этом году. */
  countries: string[];
  /** Страны, где до этого года не был ни разу. */
  newCountries: string[];
  airports: number;
  airlines: number;
  longest: { depIata: string; arrIata: string; distanceKm: number; date: string } | null;
  topAirport: { iata: string; city: string; flights: number } | null;
  /** 1–12; null, если месяцы равны по числу рейсов только с одним рейсом. */
  busiestMonth: { month: number; flights: number } | null;
  previous: { flights: number; distanceKm: number } | null;
}

/** Годы, в которых есть рейсы, от свежего к старому. */
export function flightYears(rows: YearRow[]): number[] {
  return [...new Set(rows.map((row) => Number(row.depLocalDate.slice(0, 4))))]
    .filter(Number.isFinite)
    .sort((a, b) => b - a);
}

export function yearSummary(rows: YearRow[], year: number): YearSummary {
  const sorted = [...rows].sort((a, b) => a.depLocalDate.localeCompare(b.depLocalDate));
  const inYear = sorted.filter((row) => Number(row.depLocalDate.slice(0, 4)) === year);
  const before = new Set<string>();
  for (const row of sorted) {
    if (Number(row.depLocalDate.slice(0, 4)) >= year) break;
    before.add(row.depCountry);
    before.add(row.arrCountry);
  }

  const countries: string[] = [];
  const airports = new Map<string, { iata: string; city: string; flights: number }>();
  const airlines = new Set<string>();
  const months = new Map<number, number>();
  let distanceKm = 0;
  let durationMin = 0;
  let estimated = false;
  let longest: YearSummary['longest'] = null;

  for (const row of inYear) {
    distanceKm += row.distanceKm ?? 0;
    durationMin += row.durationMin ?? 0;
    if (row.estimated) estimated = true;
    if (row.airlineCode) airlines.add(row.airlineCode);

    for (const country of [row.depCountry, row.arrCountry]) {
      if (!countries.includes(country)) countries.push(country);
    }
    for (const [iata, city] of [
      [row.depIata, row.depCity],
      [row.arrIata, row.arrCity],
    ] as const) {
      const bucket = airports.get(iata) ?? { iata, city, flights: 0 };
      bucket.flights += 1;
      airports.set(iata, bucket);
    }

    const month = Number(row.depLocalDate.slice(5, 7));
    months.set(month, (months.get(month) ?? 0) + 1);

    if (row.distanceKm && (!longest || row.distanceKm > longest.distanceKm)) {
      longest = {
        depIata: row.depIata,
        arrIata: row.arrIata,
        distanceKm: row.distanceKm,
        date: row.depLocalDate,
      };
    }
  }

  const topAirport =
    [...airports.values()].sort((a, b) => b.flights - a.flights || a.iata.localeCompare(b.iata))[0] ??
    null;
  const [busyMonth, busyFlights] = [...months.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0] ?? [];
  const lastYear = sorted.filter((row) => Number(row.depLocalDate.slice(0, 4)) === year - 1);

  return {
    year,
    flights: inYear.length,
    distanceKm,
    durationMin,
    estimated,
    countries,
    newCountries: countries.filter((country) => !before.has(country)),
    airports: airports.size,
    airlines: airlines.size,
    longest,
    topAirport,
    busiestMonth: busyMonth && busyFlights && busyFlights > 1 ? { month: busyMonth, flights: busyFlights } : null,
    previous:
      lastYear.length > 0
        ? {
            flights: lastYear.length,
            distanceKm: lastYear.reduce((sum, row) => sum + (row.distanceKm ?? 0), 0),
          }
        : null,
  };
}
